import React, { useState, useMemo, useEffect } from 'react';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { FormSheet } from '@/components/ui/FormSheet';
import type { TabDef } from '@/components/ui/FormSheet';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { showToast } from '@/components/ui/Toast';
import { RadioGroup } from '@/components/ui/Radio/Radio';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosActorCreateReadinessAssessment } from '@/hooks/roros/actors';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { CreateReadinessAssessmentPayload } from '@/types/roros/actors';
import { t, onDictionaryChange } from '@/lib/i18n';

/**
 * ReadinessAssessmentDialog — promote-flow wizard for AI Operators.
 *
 * Slide-out Sheet variant (Wave 3 of the dialogs-to-sheets UX refactor). Wave
 * 1 FormSheet refactor: all sheet chrome (header, discard banner, body) is now
 * handled by FormSheet. Stays as a Sheet (grandfathered R4 exception per design
 * contract §2). Two tabs split the long form:
 *
 *   Tab 1 — "Context": Domain, Assessment Type, Target Adoption Level, Notes
 *   Tab 2 — "Assessment": 12 yes/no questions grouped by their 4 indicators
 *
 * The live Score: X/12 badge in the sticky header is passed via `headerExtras`
 * and is always visible from both tabs. The "still to do" cue (N/12) is
 * included in the Assessment tab label — a lightweight progress counter
 * distinct from the dirty-dot so the two states are not confused at a glance.
 *
 * Dirty tracking: FormSheet handles the discard banner and dismiss guard.
 * The consumer owns the FormSnapshot type and per-field diff function.
 *
 * Submit payload is unchanged from the Modal version — canonical English
 * `indicator` and `question` strings are preserved verbatim so server-side
 * storage stays stable.
 */

interface ReadinessAssessmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  actor: RorosActorDTO;
}

// ASSESSMENT_TYPE_OPTIONS and TARGET_LEVEL_OPTIONS are now computed inside the
// component (via useMemo) so t() is called at render time, not at module load.
// This ensures locale changes propagate correctly to option labels. (AC-PW1-3-1)

const LEVEL_ORDER: Record<string, number> = {
  shadow: 0,
  copilot: 1,
  autopilot: 2,
};

/** The next level above `current` — what we default the target picker to. */
function defaultTargetLevel(current: string | null | undefined): 'shadow' | 'copilot' | 'autopilot' {
  if (current === 'shadow') return 'copilot';
  if (current === 'copilot') return 'autopilot';
  // No current level (or already autopilot) → default to shadow as a safe fallback.
  return 'shadow';
}

interface QuestionDef {
  indicator: string;
  question: string;
  /** D.3.1 — translation key for the question text (namespace: ui). */
  tKey: string;
}

/**
 * D.3.1 — Maps indicator display name → translation key.
 * The `indicator` string is the canonical API value stored in responses.
 * The `tKey` is the i18n key for the localized label shown in the UI.
 */
const INDICATOR_TKEYS: Record<string, string> = {
  'Process Readiness':   'roros.readiness.indicators.process_readiness',
  'Data Readiness':      'roros.readiness.indicators.data_readiness',
  'Tool Readiness':      'roros.readiness.indicators.tool_readiness',
  'Trust & Governance':  'roros.readiness.indicators.trust_governance',
};

const QUESTIONS: QuestionDef[] = [
  // Indicator 1: Process Readiness
  { indicator: 'Process Readiness', tKey: 'roros.readiness.questions.process_1', question: "Is the domain's workflow documented and understood by the team?" },
  { indicator: 'Process Readiness', tKey: 'roros.readiness.questions.process_2', question: 'Are WIP limits defined and respected?' },
  { indicator: 'Process Readiness', tKey: 'roros.readiness.questions.process_3', question: 'Are escalation paths clear and tested?' },
  // Indicator 2: Data Readiness
  { indicator: 'Data Readiness', tKey: 'roros.readiness.questions.data_1', question: 'Is relevant data accessible and up-to-date?' },
  { indicator: 'Data Readiness', tKey: 'roros.readiness.questions.data_2', question: 'Are data quality standards defined?' },
  { indicator: 'Data Readiness', tKey: 'roros.readiness.questions.data_3', question: 'Are data pipelines reliable and monitored?' },
  // Indicator 3: Tool Readiness
  { indicator: 'Tool Readiness', tKey: 'roros.readiness.questions.tool_1', question: 'Are the required tools available and configured?' },
  { indicator: 'Tool Readiness', tKey: 'roros.readiness.questions.tool_2', question: 'Has the team been trained on tool usage?' },
  { indicator: 'Tool Readiness', tKey: 'roros.readiness.questions.tool_3', question: 'Are tool integrations tested and stable?' },
  // Indicator 4: Trust & Governance
  { indicator: 'Trust & Governance', tKey: 'roros.readiness.questions.governance_1', question: 'Are role contracts defined for AI operators in this domain?' },
  { indicator: 'Trust & Governance', tKey: 'roros.readiness.questions.governance_2', question: 'Are decision rights documented for this domain?' },
  { indicator: 'Trust & Governance', tKey: 'roros.readiness.questions.governance_3', question: 'Does the team understand and accept AI operator boundaries?' },
];

const INDICATORS = ['Process Readiness', 'Data Readiness', 'Tool Readiness', 'Trust & Governance'];

type AssessmentType = 'onboarding' | 'adoption_change';
type AdoptionTarget = 'shadow' | 'copilot' | 'autopilot';

// Snapshot of the 5 form fields we diff against to compute the dirty flag
// and the per-tab dirty dots.
type FormSnapshot = {
  domainId: string;
  assessmentType: AssessmentType;
  targetAdoptionLevel: AdoptionTarget;
  answers: Record<number, boolean>;
  notes: string;
};

// Per-field diff — returns one boolean per field. FormSheet uses this to
// compute isDirty and per-tab dirty dots via TabDef.fields.
function diffSnapshots(base: FormSnapshot, cur: FormSnapshot): Partial<Record<keyof FormSnapshot, boolean>> {
  const answersDiff = (() => {
    const ak = Object.keys(base.answers);
    const bk = Object.keys(cur.answers);
    if (ak.length !== bk.length) return true;
    for (const k of ak) {
      const idx = k as unknown as number;
      if (base.answers[idx] !== cur.answers[idx]) return true;
    }
    return false;
  })();

  return {
    domainId: base.domainId !== cur.domainId,
    assessmentType: base.assessmentType !== cur.assessmentType,
    targetAdoptionLevel: base.targetAdoptionLevel !== cur.targetAdoptionLevel,
    answers: answersDiff,
    notes: base.notes !== cur.notes,
  };
}

// Which FormSnapshot fields belong to each tab — consumed by FormSheet's
// TabDef.fields to drive the per-tab dirty dots.
const CONTEXT_FIELDS: Array<keyof FormSnapshot> = ['domainId', 'assessmentType', 'targetAdoptionLevel', 'notes'];
const QUESTIONS_FIELDS: Array<keyof FormSnapshot> = ['answers'];

export function ReadinessAssessmentDialog({ isOpen, onClose, actor }: ReadinessAssessmentDialogProps) {
  const domainsQuery = useRorosDomains();
  const createMutation = useRorosActorCreateReadinessAssessment();

  // Re-resolve option labels when dictionary changes (locale switch). (AC-PW1-3-1)
  // Named `dictVersion` (no leading underscore) for consistency with WeeklyReviewPage.tsx.
  const [dictVersion, setDictVersion] = useState(0);
  useEffect(() => onDictionaryChange(() => setDictVersion((v) => v + 1)), []);

  const ASSESSMENT_TYPE_OPTIONS = useMemo(() => [
    { value: 'onboarding', label: t('Onboarding') },
    { value: 'adoption_change', label: t('Adoption Change') },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [dictVersion]);

  const TARGET_LEVEL_OPTIONS = useMemo(() => [
    { value: 'shadow', label: t('Shadow') },
    { value: 'copilot', label: t('Copilot') },
    { value: 'autopilot', label: t('Autopilot') },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [dictVersion]);

  // ─── Form state via useFormSnapshot (F-Refinement-10) ─────────────────────
  // depKey combines actor.id + actor.adoptionLevel to match original seed deps.
  const { state, setField, baseline, current, setBaseline } = useFormSnapshot<FormSnapshot>({
    initialFactory: () => ({
      // Use 'none' sentinel instead of '' — avoids passing empty string as Select value (§12.6 rule 9).
      domainId: 'none',
      assessmentType: 'onboarding' as AssessmentType,
      targetAdoptionLevel: defaultTargetLevel(actor.adoptionLevel),
      answers: {},
      notes: '',
    }),
    isOpen,
    depKey: `${actor.id}-${actor.adoptionLevel}`,
  });
  const { domainId, assessmentType, targetAdoptionLevel, answers, notes } = state;

  const actorDomainIds = new Set(actor.domains?.map((d) => d.domainId) ?? []);
  const availableDomains = (domainsQuery.data ?? [])
    .filter((d) => d.isActive && actorDomainIds.has(d.id))
    .map((d) => ({ value: d.id, label: d.name }));

  /**
   * Target options must be strictly higher than the actor's current adoption
   * level — assessing "readiness to stay where I am" is meaningless, and the
   * GOV-04 gate only consumes target-level results that represent a promotion.
   * If the actor has no adoption level yet, we allow the full range.
   */
  const currentLevelOrder = actor.adoptionLevel != null ? LEVEL_ORDER[actor.adoptionLevel] ?? -1 : -1;
  const targetLevelOptions = TARGET_LEVEL_OPTIONS.filter(
    (opt) => LEVEL_ORDER[opt.value] > currentLevelOrder,
  );

  const handleAnswer = (index: number, value: boolean) => {
    setField('answers', { ...answers, [index]: value });
  };

  const score = QUESTIONS.filter((_, i) => answers[i] === true).length;
  const maxScore = QUESTIONS.length;
  const answeredCount = QUESTIONS.filter((_, i) => answers[i] !== undefined).length;
  const allAnswered = answeredCount === maxScore;

  const validDomain = !!domainId && domainId !== 'no-domains' && domainId !== 'none';

  const handleSubmit = () => {
    if (!validDomain) {
      showToast.error(t('Please select a domain'));
      return;
    }
    if (!allAnswered) {
      showToast.error(t('Please answer all questions'));
      return;
    }

    const responses = QUESTIONS.map((q, i) => ({
      indicator: q.indicator,
      question: q.question,
      answer: answers[i] ?? false,
    }));

    const payload: CreateReadinessAssessmentPayload = {
      domainId: domainId, // A1: camelCase on API surface
      assessment_type: assessmentType,
      target_adoption_level: targetAdoptionLevel,
      responses,
      score,
      max_score: maxScore,
      notes: notes.trim() || undefined,
    };

    createMutation.mutate(
      { actorId: actor.id, payload },
      {
        onSuccess: () => {
          showToast.success(t('Readiness assessment submitted'));
          // Successful save consumed the dirty state — bypass the guard.
          setBaseline(current);
          onClose();
        },
        onError: (err) => {
          // Sheet stays open with state intact so the user can retry. The
          // dirty flag remains true so a follow-up dismiss attempt still
          // surfaces the discard confirm.
          showToast.error(err instanceof Error ? err.message : t('Failed to submit assessment'));
        },
      },
    );
  };

  // Live score badge — passed to FormSheet as headerExtras so it's always
  // visible in the sticky header from both tabs, before the Cancel/Save buttons.
  const scoreBadge = (
    <Badge variant="secondary" data-testid="readiness-assessment-score-badge">
      {t('Score')}: {score}/{maxScore}
    </Badge>
  );

  // "Still to do" cue on Tab 2 — appended as a small `(N/12)` text fragment
  // when not yet all answered. Deliberately a different visual from the
  // dirty-dot above so the two states cannot be confused at a glance.
  const questionsProgressFragment = !allAnswered ? (
    <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">
      ({answeredCount}/{maxScore})
    </span>
  ) : null;

  // ─── Tab definitions ──────────────────────────────────────────────────────

  const tab1Content = (
    <div className="space-y-6">
      {/* Domain select */}
      <div data-testid="readiness-assessment-domain-select">
        <Select
          label={t('Domain')}
          options={[
            { value: 'none', label: t('Select a domain...') },
            ...(availableDomains.length > 0
              ? availableDomains
              : [{ value: 'no-domains', label: t('No domains assigned') }]),
          ]}
          value={domainId}
          onValueChange={(v) => setField('domainId', v)}
        />
      </div>

      {/* Assessment type */}
      <Select
        label={t('Assessment Type')}
        options={ASSESSMENT_TYPE_OPTIONS}
        value={assessmentType}
        onValueChange={(v) => setField('assessmentType', v as AssessmentType)}
      />

      {/* Target adoption level — only levels strictly above current. */}
      <div data-testid="readiness-assessment-target-level-select">
        <Select
          label={t('Target Adoption Level')}
          options={targetLevelOptions}
          value={targetAdoptionLevel}
          onValueChange={(v) => setField('targetAdoptionLevel', v as AdoptionTarget)}
        />
      </div>

      {/* Notes */}
      <Textarea
        label={t('Notes (optional)')}
        value={notes}
        onChange={(e) => setField('notes', e.target.value)}
        placeholder={t('Additional observations or context...')}
        rows={3}
      />
    </div>
  );

  const tab2Content = (
    <div className="space-y-6">
      {INDICATORS.map((indicator) => {
        const indicatorQuestions = QUESTIONS.filter((q) => q.indicator === indicator);
        return (
          <div key={indicator}>
            {/* D.3.1 — translate indicator label; canonical English stored in `indicator` for API */}
            <h4 className="text-sm font-semibold mb-3">
              {t(INDICATOR_TKEYS[indicator])}
            </h4>
            <div className="space-y-3">
              {indicatorQuestions.map((q) => {
                const globalIndex = QUESTIONS.indexOf(q);
                const currentAnswer = answers[globalIndex];
                return (
                  <div key={globalIndex} className="p-3 bg-muted rounded-lg">
                    {/* D.3.1 — translate question text; canonical English in q.question for API storage */}
                    <p className="text-sm mb-2">{t(q.tKey)}</p>
                    {/*
                      D.3.2 + D.3.3 — DS RadioGroup. RadioGroup provides
                      role="radiogroup", proper focus management, and
                      label association — satisfying D.3.3 accessibility
                      requirements without additional ARIA patches.
                    */}
                    <RadioGroup
                      name={`question-${globalIndex}`}
                      orientation="horizontal"
                      value={currentAnswer === true ? 'yes' : currentAnswer === false ? 'no' : undefined}
                      onChange={(val) => handleAnswer(globalIndex, val === 'yes')}
                      options={[
                        {
                          value: 'yes',
                          label: t('Yes'),
                          labelClassName: currentAnswer === true ? 'text-green-700' : undefined,
                        },
                        {
                          value: 'no',
                          label: t('No'),
                          labelClassName: currentAnswer === false ? 'text-red-700' : undefined,
                        },
                      ]}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const tabs: Array<TabDef<FormSnapshot>> = [
    {
      key: 'context',
      label: t('Context'),
      fields: CONTEXT_FIELDS,
      content: tab1Content,
      testId: 'readiness-assessment-tab-context',
    },
    {
      key: 'questions',
      label: (
        <>
          {t('Assessment')}
          {questionsProgressFragment}
        </>
      ),
      fields: QUESTIONS_FIELDS,
      content: tab2Content,
      testId: 'readiness-assessment-tab-questions',
    },
  ];

  const title = t('New Readiness Assessment');
  const subtitle = actor.name ?? undefined;

  return (
    <FormSheet<FormSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      mode="create"
      baseline={baseline}
      current={current}
      diff={diffSnapshots}
      onSubmit={handleSubmit}
      isSubmitting={createMutation.isPending}
      submitDisabled={!validDomain || !allAnswered}
      submitLabel={t('Submit Assessment')}
      submittingLabel={t('Submitting…')}
      headerExtras={scoreBadge}
      width="md"
      tabs={tabs}
      tabsAriaLabel={t('Readiness assessment sections')}
      submitTestId="readiness-assessment-submit"
    />
  );
}
